import { ElementType } from "react";
import {
  BriefcaseBusinessIcon,
  BuildingIcon,
  ChevronDownIcon,
  ClockIcon,
  FileIcon,
  FilterXIcon,
  KanbanIcon,
  SparklesIcon,
  UserIcon,
} from "lucide-react";

import {
  CandidacyStatusBadge,
  EmptyDataDash,
  EmptyState,
  OwnerAvatars,
  Row,
  Text,
} from "@/components";
import { AgenticScoreChip } from "@jobs/pipeline/shared/AgenticScoreChip";
import { LastPingCell } from "@/components/my-aviary/LastPingCell";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { agenticScoreToBucket } from "@/lib/agenticScore";
import { aviary } from "@/lib/aviary";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  MyCandidaciesGroupBy,
  MyCandidaciesQueryParams,
  MyCandidacyResponse,
} from "@/types";

// Hide redundant columns when grouping by job or status or showing specific user
const getColumnWidths = (
  groupBy: MyCandidaciesGroupBy | undefined,
  showOwner: boolean,
): string[] => {
  if (groupBy === MyCandidaciesGroupBy.Job) {
    // Hides Job, Company.
    return showOwner
      ? ["22%", "20%", "0%", "0%", "19%", "20%", "19%"]
      : ["25%", "25%", "0%", "0%", "25%", "25%"];
  }
  if (groupBy === MyCandidaciesGroupBy.Status) {
    // Hides Stage.
    return showOwner
      ? ["16%", "0%", "21%", "21%", "14%", "14%", "14%"]
      : ["18%", "0%", "26%", "26%", "15%", "15%"];
  }
  return showOwner
    ? ["15%", "13%", "18%", "18%", "11%", "12%", "13%"]
    : ["16%", "14%", "22%", "22%", "13%", "13%"];
};

const getHeader = (Icon: ElementType, label: string, width: string) => {
  return (
    <TableHead style={{ width }}>
      <Row className="items-center gap-2">
        <Icon className="h-4 w-4" />
        <Text variant="header">{label}</Text>
      </Row>
    </TableHead>
  );
};

export const MyAviaryTable = ({
  groupBy,
  handleUpdateView,
  myCandidaciesQueryParams,
  myCandidacies,
  setSelectedCandidacyId,
}: {
  groupBy?: MyCandidaciesGroupBy;
  handleUpdateView: (
    myCandidaciesQueryParams: MyCandidaciesQueryParams,
  ) => void;
  myCandidaciesQueryParams: MyCandidaciesQueryParams;
  myCandidacies: MyCandidacyResponse[];
  setSelectedCandidacyId: (candidacyId: string | null) => void;
}) => {
  const displayGroups = aviary.getMyCandidaciesDisplayGroups(
    myCandidacies,
    groupBy,
  );

  const render = () => {
    if (!groupBy || myCandidacies.length === 0) {
      return (
        <BaseMyAviaryTable
          groupBy={groupBy}
          handleClearAllFilters={handleUpdateView}
          myCandidaciesQueryParams={myCandidaciesQueryParams}
          myCandidacies={myCandidacies}
          setSelectedCandidacyId={setSelectedCandidacyId}
        />
      );
    }

    return (
      <>
        {displayGroups.map((group) => (
          <div
            key={group.key}
            className="mt-4 overflow-hidden rounded-lg border shadow-sm first:mt-0"
          >
            <Collapsible className="group/collapsible" defaultOpen>
              <CollapsibleTrigger asChild>
                <Button
                  className="flex w-full justify-between rounded-b-none bg-primary-300 p-3 hover:bg-primary-400"
                  onClick={() => {
                    mixpanelUtils.trackButtonClick(
                      "Group toggle",
                      MIXPANEL_SCREENS.MY_AVIARY,
                      { group: aviary.getGroupTitle(group, groupBy) },
                    );
                  }}
                >
                  <Row className="items-center gap-2 text-slate-700">
                    <ChevronDownIcon className="transition-transform group-data-[state=open]/collapsible:rotate-180" />
                    <span>{aviary.getGroupTitle(group, groupBy)}</span>
                  </Row>
                  <Badge variant="secondary">
                    {group.candidacies.length}{" "}
                    {group.candidacies.length === 1
                      ? "candidacy"
                      : "candidacies"}
                  </Badge>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <BaseMyAviaryTable
                  groupBy={groupBy}
                  handleClearAllFilters={handleUpdateView}
                  myCandidaciesQueryParams={myCandidaciesQueryParams}
                  myCandidacies={group.candidacies}
                  setSelectedCandidacyId={setSelectedCandidacyId}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        ))}
      </>
    );
  };

  return render();
};

const BaseMyAviaryTable = ({
  groupBy,
  handleClearAllFilters,
  myCandidaciesQueryParams,
  myCandidacies,
  setSelectedCandidacyId,
}: {
  groupBy?: MyCandidaciesGroupBy;
  handleClearAllFilters: (
    myCandidaciesQueryParams: MyCandidaciesQueryParams,
  ) => void;
  myCandidaciesQueryParams: MyCandidaciesQueryParams;
  myCandidacies: MyCandidacyResponse[];
  setSelectedCandidacyId: (candidacyId: string | null) => void;
}) => {
  const showOwner = !!myCandidaciesQueryParams.allUsers;
  const columnWidths = getColumnWidths(groupBy, showOwner);
  const { userInitialsMap } = useTaggableUsers();
  const baseColumnCount =
    groupBy === MyCandidaciesGroupBy.Job
      ? 4
      : groupBy === MyCandidaciesGroupBy.Status
        ? 5
        : 6;
  const colSpan = baseColumnCount + (showOwner ? 1 : 0);
  return (
    <div
      className={cn(
        "relative overflow-auto",
        groupBy ? "max-h-[62vh]" : "max-h-[83vh] rounded-lg border",
      )}
    >
      <table className="w-full table-fixed text-sm">
        <TableHeader className="sticky top-0 z-10 bg-slate-50 [&_tr]:border-b-0">
          <TableRow className="sticky-border-bottom">
            {getHeader(UserIcon, "Candidate", columnWidths[0])}
            {groupBy !== MyCandidaciesGroupBy.Status &&
              getHeader(KanbanIcon, "Stage", columnWidths[1])}
            {groupBy !== MyCandidaciesGroupBy.Job &&
              getHeader(BriefcaseBusinessIcon, "Job", columnWidths[2])}
            {groupBy !== MyCandidaciesGroupBy.Job &&
              getHeader(BuildingIcon, "Company", columnWidths[3])}
            {getHeader(SparklesIcon, "Agent Score", columnWidths[4])}
            {getHeader(ClockIcon, "Last Ping", columnWidths[5])}
            {showOwner && getHeader(UserIcon, "Owner", columnWidths[6])}
          </TableRow>
        </TableHeader>
        <TableBody>
          {myCandidacies.length > 0 ? (
            myCandidacies.map((candidacy) => {
              const bucket = agenticScoreToBucket(
                candidacy.agenticScores[0]?.toScore,
              );
              return (
                <TableRow
                  className="cursor-pointer"
                  key={candidacy.candidacyId}
                  onClick={() => {
                    mixpanelUtils.trackRowClick(
                      "My Aviary candidacy",
                      MIXPANEL_SCREENS.MY_AVIARY,
                    );
                    setSelectedCandidacyId(candidacy.candidacyId);
                  }}
                >
                  <TableCell style={{ width: columnWidths[0] }}>
                    <Text>
                      {candidacy.firstName && candidacy.lastName
                        ? misc.toTitleCase(
                            `${candidacy.firstName} ${candidacy.lastName}`,
                          )
                        : "Unknown"}
                    </Text>
                  </TableCell>
                  {groupBy !== MyCandidaciesGroupBy.Status && (
                    <TableCell style={{ width: columnWidths[1] }}>
                      <CandidacyStatusBadge
                        byAgent={candidacy.lastStatusChangeByAgent}
                        status={candidacy.status}
                      />
                    </TableCell>
                  )}
                  {groupBy !== MyCandidaciesGroupBy.Job && (
                    <TableCell style={{ width: columnWidths[2] }}>
                      <Text>{candidacy.jobTitle}</Text>
                    </TableCell>
                  )}
                  {groupBy !== MyCandidaciesGroupBy.Job && (
                    <TableCell style={{ width: columnWidths[3] }}>
                      <Text>{candidacy.companyName}</Text>
                    </TableCell>
                  )}
                  <TableCell style={{ width: columnWidths[4] }}>
                    {bucket ? (
                      <AgenticScoreChip
                        bucket={bucket}
                        isHumanRated={
                          candidacy.agenticScores[0]?.userId != null
                        }
                        justification={candidacy.agenticScores[0]?.justification}
                      />
                    ) : (
                      <EmptyDataDash />
                    )}
                  </TableCell>
                  <TableCell style={{ width: columnWidths[5] }}>
                    <LastPingCell lastPing={candidacy.lastPing} />
                  </TableCell>
                  {showOwner && (
                    <TableCell style={{ width: columnWidths[6] }}>
                      <OwnerAvatars
                        ownerUserIds={candidacy.owners.map((o) => o.userId)}
                        userInitialsMap={userInitialsMap}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell className="p-0" colSpan={colSpan}>
                <EmptyState
                  className="bg-slate-50 py-14"
                  description="No candidacies were found. Try clearing some filters to see candidacies!"
                  Icon={FileIcon}
                  primaryButtonName="Clear All Filters"
                  PrimaryButtonIcon={FilterXIcon}
                  handlePrimaryClick={() => {
                    handleClearAllFilters({
                      ...myCandidaciesQueryParams,
                      statuses: [],
                    });
                  }}
                  title="No candidacies found"
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </table>
    </div>
  );
};
